import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Stack, IconButton, Chip, Alert } from '@mui/material';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import CustomAutoComplete from '../../../../../Components/CustomAutoComplete';
import { AppColors } from '../../../../../Utils/helper/AppColors';
import { useSelector } from 'react-redux';
import CultService from '../../../../../Services/CultService';
import { timezones } from '../../../../../Utils/timezones';
// import { Add, DeleteOutline } from '@mui/icons-material';
import { FILTER_OPTIONS, DATE_CONDITIONS, SOURCES } from '../../../../../Utils/helper/AppConstants';
import moment from 'moment';

export default function AutomationRuleFilters({ details, setDetails , setIsDialogOpenCheckData }) {
	const company = useSelector((state) => state?.app?.company)?._id;

	const [currentRule, setCurrentRule] = useState({
		filterType: null,
		condition: null,
		value: null,
	});

	// console.log("currentRule",currentRule);

	const [depts, setDepts] = useState([]);
	const [tZ, setTZ] = useState(timezones.map((i) => ({ label: i.zone, value: i.zone })));

	const [usedFilters, setUsedFilters] = useState([]);
	// console.log("usedFilters", usedFilters);

	const getDepts = async () => {
		let res = await CultService.getFilterValues({ params: { companyId: company } });

		if (res?.status === 200) {
			let departments = res?.data?.departments || [];
			setDepts(departments.map((dept) => ({ label: dept, value: dept })));
		}
	};

	// Initialize used filters from existing data
	// useEffect(() => {
	// 	if (details?.advancedOptions?.filters) {
	// 		const used = [];
	// 		Object.keys(details.advancedOptions.filters).forEach((key) => {
	// 			if(key !== 'dateOfJoining' && key !== 'dateOfRelieving' && key !== 'source'){
	// 				details.advancedOptions.filters[key]?.value?.length > 0 && used.push(key);
	// 			}
	// 			else{
	// 			used.push(key);

	// 			}
	// 		});
	// 		setUsedFilters(used);
	// 	}
	// }, []);

	useEffect(() => {
		getDepts();
	}, []);

	// Modify getAvailableFilters function to handle dateOfJoining special case
	const getAvailableFilters = () => { 
		// Special handling for dateOfJoining which can have both before and after
		const existingAllFilters = details?.advancedOptions?.filters || {};
		// console.log("Existing All Filters:", existingAllFilters);
		const existingDateFilters = details?.advancedOptions?.filters?.dateOfJoining || {};
		const existingDateFiltersRelieving = details?.advancedOptions?.filters?.dateOfRelieving || {};
		const dateConditionsUsed = Object.keys(existingDateFilters);
		const dateConditionsUsedRelieving = Object.keys(existingDateFiltersRelieving);

		// Get all other existing filters 
		const existingOtherFilters = Object.keys(details?.advancedOptions?.filters || {}).filter(key => key !== 'dateOfJoining' && key !== 'dateOfRelieving');

		// console.log("Existing Other Filters:", existingOtherFilters);

		// If both date conditions are already used, filter out dateOfJoining
		const shouldExcludeDateOfJoining = dateConditionsUsed.includes('before') && dateConditionsUsed.includes('after');

		// If both date conditions are already used, filter out dateOfRelieving
		const shouldExcludeDateOfRelieving = dateConditionsUsedRelieving.includes('before') && dateConditionsUsedRelieving.includes('after');

		let availableFilters = FILTER_OPTIONS.filter((option) => {
			// For dateOfJoining, only exclude if both conditions are used
			if (option.value === 'dateOfJoining') {
				return !shouldExcludeDateOfJoining;
			}
			else if (option.value === 'dateOfRelieving') {
				return !shouldExcludeDateOfRelieving;
			}
			else if(option.value !== 'source') {
				 return (!existingAllFilters?.[option.value] && !(existingAllFilters?.[option.value]?.length > 0));
			}

			// For other filters, exclude if they're already used
			return !existingOtherFilters.includes(option.value);
		});

		// console.log("Available Filters:", availableFilters);
		return availableFilters;
	};

	// Add a new function to get available conditions for dateOfJoining / dateOfRelieving
	const getAvailableDateConditions = () => {

		let usedConditions = [];
		if(currentRule.filterType === 'dateOfJoining') {
		const existingDateFilters = details?.advancedOptions?.filters?.dateOfJoining || {};
		 usedConditions = Object.keys(existingDateFilters);
		}
		else if(currentRule.filterType === 'dateOfRelieving') {
			const existingDateFilters = details?.advancedOptions?.filters?.dateOfRelieving || {};
			usedConditions = Object.keys(existingDateFilters);
		}

		return DATE_CONDITIONS.filter((condition) => !usedConditions.includes(condition.value));
	};


	// Check if the current rule is complete and valid
	const isRuleValid = () => {
    // First check if filterType is selected
    if (!currentRule.filterType) {
        return false;
    }

    // Handle dateOfJoining - needs filterType, condition, and value
    if (currentRule.filterType === 'dateOfJoining' || currentRule.filterType === 'dateOfRelieving') {
        return currentRule.condition && currentRule.value;
    }
    
    // Handle department and timezone - need filterType and non-empty array
    if (currentRule.filterType === 'department' || currentRule.filterType === 'timezone') {
        return currentRule.value && Array.isArray(currentRule.value) && currentRule.value.length > 0;
    }

    // Handle source  - needs filterType and value
    if (currentRule.filterType === 'source') {
        return currentRule.value;
    }

    // Fallback for any other filter types
    return false;
};

	// Add the current rule to the filters
	const handleAddRule = () => {
    if (isRuleValid()) {
        const newFilters = { ...(details?.advancedOptions?.filters || {}) };

        if (currentRule.filterType === 'dateOfJoining' || currentRule.filterType === 'dateOfRelieving') {
            // Existing date of joining logic
            newFilters[currentRule.filterType] = {
                ...(newFilters[currentRule.filterType] || {}),
                [currentRule.condition]: currentRule.value,
            };

            // Only remove Date of Joining / Date of Relieving from available filters if both conditions are used
            const dateConditions = Object.keys(newFilters[currentRule.filterType]);
            if (dateConditions.includes('before') && dateConditions.includes('after')) {
                setUsedFilters([...usedFilters, currentRule.filterType]);
            }
        } else {
            // For department and timezone, always store as array of values (not objects)
            // For other fields, store as single value
            let valueToStore;
            
            if (currentRule.filterType === 'department' || currentRule.filterType === 'timezone') {
                // Extract just the values from the array of objects
                if (Array.isArray(currentRule.value)) {
                    valueToStore = currentRule.value.map(item => 
                        typeof item === 'object' && item !== null ? item.value : item
                    );
                } else if (typeof currentRule.value === 'object' && currentRule.value !== null) {
                    valueToStore = [currentRule.value.value];
                } else if (currentRule.value) {
                    valueToStore = [currentRule.value];
                } else {
                    valueToStore = [];
                }
            } else {
                // For single values
                valueToStore = typeof currentRule.value === 'object' && currentRule.value !== null 
                    ? currentRule.value.value 
                    : currentRule.value;
            }
                    
            newFilters[currentRule.filterType] = {
                value: valueToStore
            };
            setUsedFilters([...usedFilters, currentRule.filterType]);
        }

        setDetails({
            ...details,
            advancedOptions: {
                ...(details?.advancedOptions || {}),
                filters: newFilters,
            },
        });

        setCurrentRule({ filterType: null, condition: null, value: null });
    }
};

	// Remove a filter
	const handleRemoveFilter = (filterType, condition) => {
		// Update this to use advancedOptions path
		const newFilters = { ...(details?.advancedOptions?.filters || {}) };

		if ((filterType === 'dateOfJoining' || filterType === 'dateOfRelieving') &&  condition) {
			const dateFilter = { ...newFilters[filterType] };
			delete dateFilter[condition];

			if (Object.keys(dateFilter).length === 0) {
				delete newFilters[filterType];
				setUsedFilters(usedFilters.filter((f) => f !== filterType));
			} else {
				newFilters[filterType] = dateFilter;
			}
		} else {
			delete newFilters[filterType];
			setUsedFilters(usedFilters.filter((f) => f !== filterType));
		}

		// Update this to use advancedOptions path
		setDetails({
			...details,
			advancedOptions: {
				...(details?.advancedOptions || {}),
				filters: newFilters,
			},
		});
	};

	// Get condition options based on selected filter
	const getConditionOptions = () => {
		if (!currentRule.filterType) return [];

		switch (currentRule.filterType) {
			case 'dateOfJoining':
			case 'dateOfRelieving':
				return getAvailableDateConditions();
			case 'department':
				return depts;
			case 'timezone':
				return tZ;
			case 'source':
				return SOURCES;
			default:
				return [];
		}
	};

	// Render value input based on filter and condition
	const renderValueInput = () => {
		if (!currentRule.filterType || (currentRule.filterType === 'dateOfJoining' && !currentRule.condition) || (currentRule.filterType === 'dateOfRelieving' && !currentRule.condition)) {
			return null;
		}

		if (currentRule.filterType === 'dateOfJoining' || currentRule.filterType === 'dateOfRelieving') {
			return (
				<DatePicker
				    format="MM-DD-YYYY"
					value={currentRule.value ? dayjs(currentRule.value) : null}
					onChange={(date) =>
						setCurrentRule({
							...currentRule,
							value: date ? date.format('YYYY-MM-DD') : null,
						})
					}
					 disabledDate={(current) => {
                    // If condition is "after", disable dates before today
                    if (currentRule?.condition === 'after') {
                        return current && current < dayjs().startOf('day');
                    }
                }}
					style={{
						width: 348,
						height: 40,
						borderRadius: '5px',
					}}
				/>
			);
		}

		// For other filters, we already selected the value in the condition dropdown
		return null;
	};

	// Create readable text for existing filters
	const getFilterChips = () => {
    const chips = [];

    if (!details?.advancedOptions?.filters) return chips;

    Object.entries(details.advancedOptions.filters).forEach(([filterType, filterData]) => {
        const filterName = FILTER_OPTIONS.find((f) => f.value === filterType)?.label || filterType;

        if (filterType === 'dateOfJoining' || filterType === 'dateOfRelieving') {
            Object.entries(filterData).forEach(([condition, value]) => {
                const conditionName = DATE_CONDITIONS.find((c) => c.value === condition)?.label || condition;
                chips.push({
                    id: `${filterType}-${condition}`,
                    label: `${filterName} ${conditionName} ${moment(value).format('MM-DD-YYYY')}`,
                    filterType,
                    condition,
                });
            });
        } else if (filterType === 'department' || filterType === 'timezone') {
            // Handle both array and string values
            const values = filterData.value;
            const displayValue = Array.isArray(values) 
                ? values.join(', ') 
                : values;
            if (displayValue.length === 0) return; // Skip empty values
	        
            chips.push({
                id: filterType,
                label: `${filterName}: ${displayValue}`,
                filterType,
            });
        } else {
            const valueName = filterData.value;
            chips.push({
                id: filterType,
                label: `${filterName}: ${valueName}`,
                filterType,
            });
        }
    });

    return chips;
};

    const handleOpenWarningDialog = (open) => {



		let checkedCurrentRule = { ...currentRule , condition: currentRule.condition ? currentRule.condition : null , value: Array.isArray(currentRule.value) ? currentRule?.value?.map(val => val.value) :  currentRule.condition ? [moment(currentRule.value).format('MM-DD-YYYY')] : [currentRule.value] };
        

        // console.log('checkedCurrentRule:', checkedCurrentRule , currentRule);
		// console.log('open:', open);
		if(open){
        setIsDialogOpenCheckData(checkedCurrentRule);

		}
		else{
			setIsDialogOpenCheckData(null);
		}
    };

    useEffect(() => {
	   if((currentRule?.value ? ( Array.isArray(currentRule.value) ? currentRule.value.length > 0 : currentRule.value ) : false) || currentRule?.condition || currentRule?.filterType){ 
		   handleOpenWarningDialog(true);
	   }
	   else{
		  handleOpenWarningDialog(false);
	   }
	}, [currentRule?.value , currentRule?.condition , currentRule?.filterType]);
	return (
		<Box
			sx={{
				mt: 2,
				mb: '100px',
			}}
		>
			{/* Header with Title and Add Rule button */}
			<Stack direction="row" justifyContent="space-between" alignItems="center" >
				<Typography
					sx={{
						fontFamily: 'Poppins',
						fontWeight: 600,
						fontSize: 20,
						color: AppColors.HEADING_TITLE4,
					}}
				>
					Automation Rule Filters
				</Typography>
			</Stack>

			<Alert severity="info" sx={{ mb: 2, fontSize: 14, padding: 1, background: 'none', '& .MuiAlert-icon': { color: '#42a5f5', marginRight: 1 } }}>
				Add your own custom rules - single or multiple. Employees must meet all rules to join this campaign.
			</Alert>

			{/* Existing Filters */}
				<Box mb={3}>
					<Typography
						sx={{
							fontFamily: 'Poppins',
							fontWeight: 500,
							fontSize: 16,
							mb: 1,
						}}
					>
						Active Filters
					</Typography>

					{getFilterChips().length === 0 ? (
						<Typography
							sx={{
								fontFamily: 'Poppins',
								fontWeight: 400,
								fontSize: 14,
								color: '#888',
								mb: 1,
							}}
						>
							No filters selected
						</Typography>
					) : (
						<Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
							{getFilterChips()?.map((chip) => (
								<Chip
									key={chip?.id}
									label={chip?.label}
									onDelete={() => handleRemoveFilter(chip?.filterType, chip?.condition)}
									sx={{
										backgroundColor: AppColors?.orange || '#F3F0FF',
										color: AppColors?.white || '#000',
										borderRadius: '4px',
										height: 'auto',
										padding: '8px 4px',
										'& .MuiChip-label': {
											fontFamily: 'Poppins',
											fontWeight: 400,
											fontSize: 14,
										},
										'& .MuiChip-deleteIcon': {
											color: AppColors.PRIMARY,
										},
									}}
								/>
							))}
						</Stack>
					)}
				</Box>

			{/* Rule Builder */}
			<Box>
				<Typography
					sx={{
						fontFamily: 'Poppins',
						fontWeight: 500,
						fontSize: 16,
						mb: 2,
					}}
				>
					{getFilterChips().length > 0 ? 'Add Another Filter' : 'Create Filter'}
				</Typography>

				<Stack direction="row" spacing={2} alignItems="center">
					{/* Filter Type */}
					<Box width={348}>
						<CustomAutoComplete
							label="Select Filter"
							options={getAvailableFilters()}
							value={currentRule.filterType ? FILTER_OPTIONS.find((opt) => opt.value === currentRule.filterType)?.label : ''}
							onChange={(val) => {
								if (val) {
									setCurrentRule({
										filterType: val.value,
										condition: null,
										value: null,
									});
								}
							}}
						/>
					</Box>

					{/* Condition - only show if filter is selected */}
					{currentRule.filterType  &&  (

						<Box width={348}>
							<CustomAutoComplete
								multiple={currentRule.filterType === 'department' || currentRule.filterType === 'timezone'}
								label={(currentRule.filterType === 'dateOfJoining' || currentRule.filterType === 'dateOfRelieving') ? 'Condition' : 'Value'}
								options={getConditionOptions()}
								value={
									currentRule.filterType === 'dateOfJoining' || currentRule.filterType === 'dateOfRelieving'
										? currentRule.condition
											? { 
													label: getConditionOptions().find((opt) => opt.value === currentRule.condition)?.label || currentRule.condition, 
													value: currentRule.condition 
												}
											: ''
										: currentRule.filterType === 'department' || currentRule.filterType === 'timezone'
										? currentRule.value || [] // Already stored as array of objects with label/value
										: currentRule.value && typeof currentRule.value === 'object'
										? currentRule.value // Already an object with label/value
										: currentRule.value
											? { 
													label: getConditionOptions().find((opt) => opt.value === currentRule.value)?.label || String(currentRule.value), 
													value: currentRule.value 
												}
											: ''
								}
								onChange={(val) => {

									// if (!val) return;

									if (currentRule.filterType === 'dateOfJoining' || currentRule.filterType === 'dateOfRelieving') {
										setCurrentRule({ ...currentRule, condition: val.value , value: null });
									} else if (currentRule.filterType === 'department' || currentRule.filterType === 'timezone') {
										// For multi-select, val is already an array of objects with label/value
										setCurrentRule({
											...currentRule,
											value: val // Store the complete objects with label/value
										});
									} else {
										setCurrentRule({ ...currentRule, value: val.value });
									}
								}}
							/>
						</Box>
					)}

					{/* Value Input - only show for date filters with condition selected */}
					{renderValueInput()}

					<Button
						variant="contained"
						color="info"
						disabled={!isRuleValid()}
						onClick={handleAddRule}
						// startIcon={<Add />}
						sx={{
							// backgroundColor: AppColors.PRIMARY,
							'&:hover': {
								// backgroundColor: AppColors.PRIMARY_DARK,
							},
							'&.Mui-disabled': {
								// backgroundColor: '#E0E0E0',
								// color: '#A0A0A0',
								border: 'none',
							},
							// borderRadius: '5px',
							// textTransform: 'none',
							// fontFamily: 'Poppins',
							// fontWeight: 500,
						}}
					>
						Add Rule
					</Button>
				</Stack>
			</Box>

		</Box>
	);
}
